import { FormEvent, useId, useState } from "react";
import { Check, LoaderCircle, RotateCcw } from "lucide-react";

type FormState = "idle" | "loading" | "success" | "error";

type DemoFormProps = {
  title: string;
  description: string;
  submitLabel: string;
  successMessage: string;
  tone?: "dark" | "light";
  fields?: Array<"name" | "email" | "phone">;
};

const fieldConfig = {
  name: { label: "Имя", type: "text", autoComplete: "name" },
  email: { label: "Email", type: "email", autoComplete: "email" },
  phone: { label: "Телефон", type: "tel", autoComplete: "tel" }
} as const;

export function DemoForm({
  title,
  description,
  submitLabel,
  successMessage,
  tone = "light",
  fields = ["name", "email"]
}: DemoFormProps) {
  const id = useId();
  const [state, setState] = useState<FormState>("idle");
  const [error, setError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      setError("Проверьте обязательные поля.");
      form.reportValidity();
      return;
    }

    setError("");
    setState("loading");
    await new Promise((resolve) => window.setTimeout(resolve, 900));

    if (!navigator.onLine) {
      setState("error");
      setError("Нет соединения. Проверьте сеть и попробуйте снова.");
      return;
    }

    setState("success");
    form.reset();
  }

  if (state === "success") {
    return (
      <div className={`demo-form demo-form--${tone} demo-form__success`} role="status">
        <Check size={28} aria-hidden="true" />
        <h3>{successMessage}</h3>
        <p>Мы отправили подтверждение на указанный контакт.</p>
        <button type="button" onClick={() => setState("idle")}>
          <RotateCcw size={16} aria-hidden="true" />
          Заполнить ещё раз
        </button>
      </div>
    );
  }

  return (
    <form className={`demo-form demo-form--${tone}`} onSubmit={handleSubmit} noValidate>
      <div className="demo-form__intro">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="demo-form__fields">
        {fields.map((field) => {
          const config = fieldConfig[field];
          const fieldId = `${id}-${field}`;
          return (
            <label key={field} htmlFor={fieldId}>
              <span>{config.label}</span>
              <input
                id={fieldId}
                name={field}
                type={config.type}
                autoComplete={config.autoComplete}
                required
                minLength={field === "name" ? 2 : undefined}
              />
            </label>
          );
        })}
      </div>
      {error && (
        <p className="demo-form__error" role="alert">
          {error}
        </p>
      )}
      <button className="demo-form__submit" type="submit" disabled={state === "loading"}>
        {state === "loading" ? (
          <>
            <LoaderCircle className="spin" size={18} aria-hidden="true" />
            Отправляем…
          </>
        ) : (
          submitLabel
        )}
      </button>
      <p className="demo-form__privacy">Нажимая кнопку, вы соглашаетесь с политикой обработки данных.</p>
    </form>
  );
}
